/**
 * Population Report UI component for PreGradeCards.
 * Fetches population data from /api/population/lookup and renders it.
 */
(function () {
    const POP_API = '/api/population/lookup';

    const gradeColors = {
        '10': '#22c55e',
        '9.5': '#16a34a',
        '9': '#65a30d',
        '8.5': '#84cc16',
        '8': '#ca8a04',
        '7': '#d97706',
        '6': '#f97316',
        '5': '#ef4444',
    };

    const companyColors = {
        'PSA': '#1d4ed8',
        'BGS': '#9333ea',
        'SGC': '#059669',
        'CGC': '#ea580c',
    };

    function getColor(grade) {
        return gradeColors[grade] || '#64748b';
    }

    function formatNumber(num) {
        return num ? Number(num).toLocaleString() : '0';
    }

    // No data → exclude the section rather than rendering an empty-state
    // message. Card-report wrappers (.ximilar-result-card, .card) hide wholly;
    // bare containers only hide when their parent has other content (a
    // population-focused page keeps its empty state via the host card).
    function hideTarget(container) {
        if (container.closest) {
            const card = container.closest('.ximilar-result-card, .card');
            if (card) return card;
        }
        const parent = container.parentElement;
        if (parent && parent !== document.body && parent.children.length > 1) {
            return container;
        }
        return null; // sole content of its parent — keep the empty state
    }

    function hideSection(container) {
        const t = hideTarget(container);
        if (t) t.style.display = 'none';
        else renderEmptyMessage(container);
    }

    function showSection(container) {
        const t = hideTarget(container);
        if (t) t.style.display = '';
    }

    function renderEmptyMessage(container) {
        container.innerHTML = `
            <div style="padding: 16px; background: var(--xg-subtle, #f8fafc); border-radius: 12px; text-align: center; color: var(--xg-muted, #64748b);">
                <p style="margin: 0 0 8px; font-size: 14px;">No population data available yet.</p>
            </div>
        `;
    }

    function renderEmpty(container) {
        container.innerHTML = '';
        hideSection(container);
    }

    function renderLoading(container) {
        container.innerHTML = `
            <div style="padding: 16px; text-align: center; color: var(--xg-muted, #64748b);">
                <svg width="24" height="24" viewBox="0 0 24 24" style="animation: spin 1s linear infinite;">
                    <circle cx="12" cy="12" r="10" stroke="var(--xg-border, #cbd5e1)" stroke-width="4" fill="none" stroke-dasharray="40" stroke-dashoffset="10"/>
                </svg>
                <p style="margin: 8px 0 0; font-size: 13px;">Loading population data...</p>
            </div>
            <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
        `;
    }

    function makeSlug(record) {
        if (!record) return '';
        const parts = [
            record.year || '',
            record.brand || '',
            record.card_number || '',
            record.player || ''
        ].map(p => String(p)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
        ).filter(p => p);
        return parts.join('-');
    }

    function groupByCompany(records) {
        const groups = {};
        records.forEach(r => {
            if (!groups[r.company]) groups[r.company] = [];
            groups[r.company].push(r);
        });
        return groups;
    }

    function renderPopulationPanel(container, records) {
        if (!records || records.length === 0) {
            renderEmpty(container);
            return;
        }

        showSection(container);

        const groups = groupByCompany(records);
        const cardName = records[0].card_name || 'Card';
        const totalPopAll = records.reduce((sum, r) => sum + (parseInt(r.population) || 0), 0);
        // total_graded is per-company, so take the max per company to avoid double-counting
        const totalGradedAll = Object.values(groups).reduce((sum, companyRecords) => {
            const companyTotal = Math.max(...companyRecords.map(r => parseInt(r.total_graded) || 0));
            return sum + companyTotal;
        }, 0);

        let html = `
            <div class="pop-report-panel" style="background: var(--xg-subtle, #0f172a); border: 1px solid var(--xg-border, transparent); border-radius: 16px; padding: 20px; color: var(--xg-text, white); margin-top: 16px;">
                <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; flex-wrap: wrap; gap: 10px;">
                    <div>
                        <h3 style="margin: 0 0 4px; color: var(--xg-text, white); font-size: 18px;">Population Report</h3>
                        <p style="margin: 0; color: var(--xg-muted, #94a3b8); font-size: 13px;">${cardName}</p>
                    </div>
                    <div style="text-align: right;">
                        <div style="font-size: 24px; font-weight: 800; color: var(--xg-accent, #38bdf8);">${formatNumber(totalPopAll)}</div>
                        <div style="font-size: 11px; color: var(--xg-muted, #94a3b8); text-transform: uppercase;">Tracked Graded</div>
                    </div>
                </div>
        `;

        Object.keys(groups).forEach(company => {
            const companyRecords = groups[company].sort((a, b) => parseFloat(b.grade) - parseFloat(a.grade));
            const companyTotal = companyRecords.reduce((sum, r) => sum + (parseInt(r.total_graded) || 0), 0);
            const companyColor = companyColors[company] || '#64748b';

            html += `
                <div style="margin-bottom: 16px;">
                    <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px;">
                        <span style="display: inline-block; padding: 3px 8px; background: ${companyColor}; border-radius: 4px; font-size: 11px; font-weight: 700;">${company}</span>
                        <span style="color: var(--xg-muted, #94a3b8); font-size: 12px;">${formatNumber(companyTotal)} total graded</span>
                    </div>
                    <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); gap: 8px;">
            `;

            companyRecords.forEach(r => {
                const gradeColor = getColor(r.grade);
                const gemRate = r.gem_rate ? `${r.gem_rate}%` : '-';
                html += `
                    <div style="background: var(--xg-chip-bg, rgba(255,255,255,0.05)); border: 1px solid var(--xg-border, rgba(255,255,255,0.1)); border-radius: 10px; padding: 12px; text-align: center; transition: transform 0.2s;" onmouseover="this.style.transform='translateY(-2px)'" onmouseout="this.style.transform='none'">
                        <div style="font-size: 12px; color: var(--xg-muted, #94a3b8); margin-bottom: 4px;">Grade</div>
                        <div style="font-size: 22px; font-weight: 800; color: ${gradeColor}; margin-bottom: 6px;">${r.grade}</div>
                        <div style="font-size: 20px; font-weight: 700; color: var(--xg-text, white);">${formatNumber(r.population)}</div>
                        <div style="font-size: 10px; color: var(--xg-muted, #64748b); margin-top: 2px;">${gemRate} gem rate</div>
                    </div>
                `;
            });

            html += `
                    </div>
                </div>
            `;
        });

        const slug = makeSlug(records[0]);
        const cardUrl = slug ? `${window.location.origin}/population/${slug}` : '';

        html += `
            <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px;">
                <button type="button" class="pop-alert-btn btn btn-sm btn-primary" style="background: #3b82f6; border: none; font-size: 12px; padding: 8px 14px; border-radius: 8px; color: white; cursor: pointer;">Set Pop Alert</button>
                <a href="/tools/pop-report?q=${encodeURIComponent(cardName)}" class="btn btn-sm btn-outline" style="border: 1px solid var(--xg-border, rgba(255,255,255,0.2)); color: var(--xg-text, white); font-size: 12px; padding: 8px 14px; border-radius: 8px; text-decoration: none; display: inline-block;">Full Report</a>
                ${cardUrl ? `<a href="/population/${slug}" class="btn btn-sm btn-outline" style="border: 1px solid var(--xg-border, rgba(255,255,255,0.2)); color: var(--xg-text, white); font-size: 12px; padding: 8px 14px; border-radius: 8px; text-decoration: none; display: inline-block;">Card Page</a>` : ''}
                ${cardUrl ? `<button type="button" class="pop-copy-link btn btn-sm btn-outline" style="border: 1px solid var(--xg-border, rgba(255,255,255,0.2)); color: var(--xg-text, white); font-size: 12px; padding: 8px 14px; border-radius: 8px; cursor: pointer; background: transparent;">Copy Link</button>` : ''}
            </div>
            <p style="margin: 12px 0 0; font-size: 11px; color: var(--xg-muted, #64748b);">Data sources: PSA, BGS, SGC, CGC population reports. Last updated today.</p>
        </div>`;

        container.innerHTML = html;

        // Update URL hash for shareable panel state (without scrolling)
        if (slug && typeof history !== 'undefined') {
            history.replaceState(null, '', '#population/' + slug);
        }

        const alertBtn = container.querySelector('.pop-alert-btn');
        if (alertBtn) {
            alertBtn.addEventListener('click', () => showAlertModal(cardName, (function(_o){return _o == null ? void 0 : _o.card_key})(records[0]) || ''));
        }

        const copyBtn = container.querySelector('.pop-copy-link');
        if (copyBtn && cardUrl) {
            copyBtn.addEventListener('click', () => {
                navigator.clipboard.writeText(cardUrl).then(() => {
                    copyBtn.textContent = 'Copied!';
                    setTimeout(() => copyBtn.textContent = 'Copy Link', 2000);
                }).catch(() => {
                    copyBtn.textContent = 'Failed';
                });
            });
        }
    }

    function showAlertModal(cardName, cardKey) {
        const modal = document.createElement('div');
        modal.className = 'pop-alert-modal';
        modal.innerHTML = `
            <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 16px;">
                <div style="background: white; border-radius: 16px; padding: 24px; max-width: 420px; width: 100%; box-shadow: 0 20px 50px rgba(0,0,0,0.3);">
                    <h3 style="margin: 0 0 8px; color: #0f172a;">Set Population Alert</h3>
                    <p style="margin: 0 0 16px; color: #64748b; font-size: 14px;">Notify me when <strong>${cardName}</strong> population changes by more than:</p>
                    <div style="margin-bottom: 16px;">
                        <select id="pop-alert-threshold" style="width: 100%; padding: 10px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 14px;">
                            <option value="1">1%</option>
                            <option value="5" selected>5%</option>
                            <option value="10">10%</option>
                            <option value="25">25%</option>
                        </select>
                    </div>
                    <div id="pop-alert-message" style="margin-bottom: 12px; font-size: 13px; min-height: 18px;"></div>
                    <div style="display: flex; gap: 10px; justify-content: flex-end;">
                        <button type="button" class="pop-alert-cancel" style="padding: 8px 16px; border: 1px solid #e2e8f0; background: white; border-radius: 8px; cursor: pointer;">Cancel</button>
                        <button type="button" class="pop-alert-save" style="padding: 8px 16px; border: none; background: #3b82f6; color: white; border-radius: 8px; font-weight: 600; cursor: pointer;">Save Alert</button>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(modal);

        modal.querySelector('.pop-alert-cancel').addEventListener('click', () => modal.remove());
        modal.querySelector('.pop-alert-save').addEventListener('click', async () => {
            const threshold = modal.querySelector('#pop-alert-threshold').value;
            const msg = modal.querySelector('#pop-alert-message');
            try {
                const resp = await fetch('/api/population/alert-subscribe', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ card_key: cardKey, card_name: cardName, threshold: parseFloat(threshold) })
                });
                const data = await resp.json();
                if (data.success) {
                    msg.style.color = '#22c55e';
                    msg.textContent = 'Alert saved!';
                    const cancelBtn = modal.querySelector('.pop-alert-cancel');
                    if (cancelBtn) cancelBtn.textContent = 'Close';
                    const saveBtn = modal.querySelector('.pop-alert-save');
                    if (saveBtn) saveBtn.disabled = true;
                } else {
                    msg.style.color = '#ef4444';
                    msg.textContent = data.error || 'Failed to save alert';
                }
            } catch (err) {
                msg.style.color = '#ef4444';
                msg.textContent = 'Network error';
            }
        });
    }

    async function fetchAndRender(container, cardKey) {
        renderLoading(container);
        try {
            const url = `${POP_API}?card_key=${encodeURIComponent(cardKey)}`;
            const resp = await fetch(url);
            const data = await resp.json();
            if (data.success && data.records) {
                renderPopulationPanel(container, data.records);
            } else {
                renderEmpty(container);
            }
        } catch (err) {
            console.error('Population lookup error:', err);
            renderEmpty(container);
        }
    }

    async function searchAndRender(container, query) {
        renderLoading(container);
        try {
            const url = `${POP_API}?q=${encodeURIComponent(query)}`;
            const resp = await fetch(url);
            const data = await resp.json();
            if (data.success && data.records && data.records.length > 0) {
                const first = data.records[0];
                const key = first.card_key;
                if (key) {
                    await fetchAndRender(container, key);
                } else {
                    renderEmpty(container);
                }
            } else {
                renderEmpty(container);
            }
        } catch (err) {
            console.error('Population search error:', err);
            renderEmpty(container);
        }
    }

    // Snapshot mounts emitted by XimilarResults.render() — the grade record
    // already carries population_records, so we render straight from the
    // embedded data. Zero network.
    function bindStaticContainer(container) {
        if (!container || container.dataset.popBound) return;
        container.dataset.popBound = '1';
        const pid = container.dataset.populationStatic;
        const records = (pid && window.XimilarResults && window.XimilarResults.getPopStatic)
            ? window.XimilarResults.getPopStatic(pid) : null;
        if (Array.isArray(records) && records.length) {
            renderPopulationPanel(container, records);
            // Snapshots aren't the live alert pipeline — drop the alert CTA.
            const alertBtn = container.querySelector('.pop-alert-btn');
            if (alertBtn) alertBtn.remove();
        } else {
            hideSection(container);
        }
    }

    function bindContainer(container) {
        if (!container || container.dataset.popBound) return;
        // Demo/mock renders (Example Results sections) carry their own static
        // population data — never fire a live API lookup for them.
        if (container.closest('[data-mock-preview]')) return;
        container.dataset.popBound = '1';
        // Hide until data arrives — an empty lookup excludes the section.
        const t = hideTarget(container);
        if (t) t.style.display = 'none';
        const key = container.dataset.populationKey;
        const query = container.dataset.populationQuery;
        if (key) fetchAndRender(container, key);
        else if (query) searchAndRender(container, query);
    }

    function init() {
        // Bind any containers already in the DOM
        document.querySelectorAll('[data-population-static]').forEach(bindStaticContainer);
        document.querySelectorAll('[data-population-key], [data-population-query]').forEach(bindContainer);

        // Result renderers (e.g. XimilarResults) inject population containers
        // after DOMContentLoaded — watch for late-added containers too.
        // [data-population-static] renders the stored snapshot; the *-key/*-query
        // attrs remain for dedicated interactive tools only.
        if (typeof MutationObserver !== 'undefined' && document.body) {
            new MutationObserver(mutations => {
                for (const m of mutations) {
                    for (const node of m.addedNodes) {
                        if (node.nodeType !== 1) continue;
                        if (node.matches && node.matches('[data-population-static]')) {
                            bindStaticContainer(node);
                        }
                        if (node.matches && node.matches('[data-population-key], [data-population-query]')) {
                            bindContainer(node);
                        }
                        if (node.querySelectorAll) {
                            node.querySelectorAll('[data-population-static]').forEach(bindStaticContainer);
                            node.querySelectorAll('[data-population-key], [data-population-query]').forEach(bindContainer);
                        }
                    }
                }
            }).observe(document.body, { childList: true, subtree: true });
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Expose for manual use
    window.PopulationReport = {
        fetchAndRender,
        searchAndRender,
        renderPopulationPanel,
        formatNumber,
    };
})();
